/**
 * API Performance View - API metrics tab for analytics
 * Shows request stats, response times, hourly distribution, top features/countries,
 * plus insights: performance alerts, error hotspots, usage patterns
 */
import { COLORS } from './shared/chartTheme.js';
import {
  TimeSeriesChart,
  HorizontalBarChart,
  HourlyDistributionChart,
  DonutChart,
} from './shared/ChartComponents.jsx';
import { formatNumber, formatEndpointLabel } from './shared/formatters.js';
import { Icons } from './shared/Icons.jsx';
import { KPICard } from './shared/KPICard.jsx';

// External dashboard URL

/**
 * Performance Alert — highlights avg response time status
 */
function PerformanceAlert({ data }) {
  const avgMs = data?.api_metrics?.avg_response_time_ms;
  if (avgMs == null) return null;

  const severity = avgMs > 500 ? 'critical' : avgMs > 200 ? 'warning' : 'success';
  const colors = { critical: '#ef4444', warning: '#f59e0b', success: '#10b981' };

  return (
    <div className="analytics-insight-card" style={{ borderLeft: `3px solid ${colors[severity]}` }}>
      <div className="insight-card-header">
        <span className="insight-card-title">Response Time: {avgMs.toFixed(0)}ms</span>
      </div>
      <p className="insight-card-description">
        {avgMs > 500
          ? 'Critically slow API responses. Performance optimization strongly recommended.'
          : avgMs > 200
            ? 'API response times above target. Identify slow endpoints.'
            : 'API performance is healthy.'}
      </p>
    </div>
  );
}

/**
 * Error Rate Alert — flags high error rates
 */
function ErrorRateAlert({ data }) {
  const errorRate = data?.api_metrics?.error_rate;
  const errorCount = data?.api_metrics?.error_count;
  if (errorRate == null) return null;

  const severity = errorRate > 5 ? 'critical' : errorRate > 1 ? 'warning' : 'success';
  const colors = { critical: '#ef4444', warning: '#f59e0b', success: '#10b981' };

  return (
    <div className="analytics-insight-card" style={{ borderLeft: `3px solid ${colors[severity]}` }}>
      <div className="insight-card-header">
        <span className="insight-card-title">
          Error Rate: {errorRate.toFixed(2)}% ({formatNumber(errorCount)} errors)
        </span>
      </div>
      <p className="insight-card-description">
        {errorRate > 5
          ? 'Critically high error rate. Review error logs and the most frequent 4xx/5xx endpoints immediately.'
          : errorRate > 1
            ? 'Error rate above target. Check the slowest and most-hit endpoints below for the source.'
            : 'Error rate is healthy. Keep monitoring.'}
      </p>
    </div>
  );
}

/**
 * Usage Pattern Insight — shows peak hour from hourly distribution
 */
function UsagePatternInsight({ data }) {
  const byHour = data?.api_metrics?.requests_by_hour;
  if (!byHour || !Object.keys(byHour).length) return null;

  const peakHour = Object.entries(byHour).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="analytics-insight-card" style={{ borderLeft: '3px solid #0d7c99' }}>
      <div className="insight-card-header">
        <span className="insight-card-title">Peak Usage: {peakHour[0]}:00</span>
      </div>
      <p className="insight-card-description">
        Highest API usage at {peakHour[0]}:00 with {formatNumber(peakHour[1])} requests.
        Optimize cache warming and scaling for these peak times.
      </p>
    </div>
  );
}

export function AnalyticsApiView({ data, periodDays }) {
  const api = data.api_metrics;

  return (
    <div className="analytics-api-view">
      <div className="analytics-kpi-grid">
        <KPICard
          icon={Icons.zap}
          label="Total Requests"
          value={formatNumber(api?.total_requests)}
          color="orange"
        />
        <KPICard
          icon={Icons.clock}
          label="Avg. Response"
          value={`${api?.avg_response_time_ms?.toFixed(0) || 0}ms`}
          color="cyan"
        />
        <KPICard
          icon={Icons.alertTriangle}
          label="Errors"
          value={formatNumber(api?.error_count)}
          sublabel={`${api?.error_rate?.toFixed(2) || 0}% Rate`}
          color="red"
        />
        <KPICard
          icon={Icons.globe}
          label="Unique IPs"
          value={formatNumber(api?.unique_ips)}
          color="green"
        />
      </div>

      {/* Insights row */}
      <div className="analytics-api-insights">
        <PerformanceAlert data={data} />
        <ErrorRateAlert data={data} />
        <UsagePatternInsight data={data} />
      </div>

      {/* API Charts */}
      <div className="analytics-charts-row">
        <TimeSeriesChart
          title="Requests per Day"
          data={api?.requests_by_day || []}
          xKey="date"
          series={[{ name: 'Requests', key: 'count', color: COLORS.orange, area: true }]}
          height={280}
          infoKey="requestsProTag"
        />
        <TimeSeriesChart
          title="Response Time Trend"
          data={api?.response_time_by_day || []}
          xKey="date"
          series={[
            { name: 'Avg (ms)', key: 'avg', color: COLORS.cyan },
            { name: 'P95 (ms)', key: 'p95', color: COLORS.orange },
          ]}
          height={280}
          infoKey="responseTimeTrend"
        />
      </div>

      <div className="analytics-charts-row">
        <HourlyDistributionChart
          title="Requests by Hour"
          data={api?.requests_by_hour || {}}
          height={250}
          infoKey="requestsNachUhrzeit"
        />
        <HorizontalBarChart
          title="Top Features"
          data={(api?.top_endpoints || []).slice(0, 8)}
          color={COLORS.orange}
          height={250}
          infoKey="topEndpoints"
          labelFormatter={formatEndpointLabel}
        />
      </div>

      <div className="analytics-charts-row">
        <HorizontalBarChart
          title="Top Countries"
          data={(api?.top_countries || []).slice(0, 8)}
          color={COLORS.green}
          height={250}
          infoKey="topLaender"
        />
        <DonutChart
          title="Auth vs Anonymous"
          data={[
            {
              name: 'Authenticated',
              value: api?.authenticated_requests || 0,
            },
            {
              name: 'Anonymous',
              value: api?.anonymous_requests || 0,
            },
          ]}
          height={250}
          infoKey="authVsAnonym"
        />
      </div>

      {/* Error Trend Chart */}
      {api?.errors_by_day && api.errors_by_day.length > 0 && (
        <div className="analytics-charts-row">
          <TimeSeriesChart
            title="Errors per Day"
            data={api.errors_by_day}
            xKey="date"
            series={[{ name: 'Errors', key: 'errors', color: COLORS.red, area: true }]}
            height={250}
            infoKey="errorsProTag"
          />
        </div>
      )}

      <div className="analytics-table-section">
        <h3>Top Features</h3>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Feature / Action</th>
              <th>Requests</th>
            </tr>
          </thead>
          <tbody>
            {(api?.top_endpoints || []).map(([endpoint, count], idx) => (
              <tr key={idx}>
                <td className="endpoint-cell" title={endpoint}>
                  {formatEndpointLabel(endpoint)}
                </td>
                <td className="count-cell">{formatNumber(count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="analytics-table-section">
        <h3>Top Countries</h3>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Country</th>
              <th>Requests</th>
            </tr>
          </thead>
          <tbody>
            {(api?.top_countries || []).map(([country, count], idx) => (
              <tr key={idx}>
                <td>{country}</td>
                <td className="count-cell">{formatNumber(count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}
